import { createFileRoute } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@/lib/server-fn-shim";
import { getMyTrip } from "@/lib/trip.functions";
import { getAdminMedia, getAdminOverview } from "@/lib/admin.functions";
import { MediaPreviewDrawer, type PreviewItem } from "@/components/MediaPreviewDrawer";
import { PageHeader } from "@/components/admin/PageHeader";
import { RoavaCard } from "@/components/roava";
import { Search, Play, ImageOff } from "lucide-react";
import { useThumbnailUrl } from "@/lib/thumbnails";
import { DayCardGallery } from "@/components/library/DayCardGallery";
import { useAuth } from "@/lib/auth";
import { TravelerVisibilityControl } from "@/components/library/TravelerVisibilityControl";
import {
  loadAdminTravelerFilter,
  saveAdminTravelerFilter,
  resolveVisibleIds,
  type TravelerFilter,
} from "@/lib/admin-traveler-filter";

export const Route = createFileRoute("/_authenticated/admin/library")({
  head: () => pageMeta({ title: 'Media Library Admin — Roava by WGP Apps', description: 'Review and moderate photos and videos uploaded across the trip.', path: '/admin/library' }),
  component: Library,
});

type Filter = "all" | "photo" | "video" | "heic";

function isHeic(name?: string | null) {
  if (!name) return false;
  return /\.(heic|heif)$/i.test(name);
}

function fmtBytes(n?: number | null) {
  if (!n) return "—";
  const mb = n / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`;
}

function Library() {
  const tripFn = useServerFn(getMyTrip);
  const mediaFn = useServerFn(getAdminMedia);
  const ovFn = useServerFn(getAdminOverview);
  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["admin-media", tripId],
    queryFn: () => mediaFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });
  const { data: overview } = useQuery({
    queryKey: ["admin-overview", tripId],
    queryFn: () => ovFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const nameById = useMemo(
    () => new Map((overview?.stats ?? []).map((s) => [s.userId, s.displayName])),
    [overview],
  );

  const [selected, setSelected] = useState<PreviewItem | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");

  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const travelerOptions = useMemo(
    () =>
      (overview?.stats ?? []).map((s) => ({
        id: s.userId,
        name: s.displayName,
      })),
    [overview],
  );
  const memberIds = useMemo(
    () => travelerOptions.map((t) => t.id),
    [travelerOptions],
  );

  const [travelerFilter, setTravelerFilter] = useState<TravelerFilter>({
    mode: "all",
  });

  // Restore the persisted admin filter once we know the trip.
  useEffect(() => {
    if (!tripId) return;
    setTravelerFilter(loadAdminTravelerFilter(tripId));
  }, [tripId]);

  // Persist on change.
  useEffect(() => {
    if (!tripId) return;
    saveAdminTravelerFilter(tripId, travelerFilter);
  }, [tripId, travelerFilter]);

  // Non-spammy filter log.
  useEffect(() => {
    const count =
      travelerFilter.mode === "all"
        ? memberIds.length
        : travelerFilter.mode === "me"
          ? currentUserId
            ? 1
            : 0
          : travelerFilter.ids.length;
    console.log("[admin-filter] selected traveler count", count);
  }, [travelerFilter, memberIds.length, currentUserId]);

  // Visible-id resolution. "Me only" with a me-id not in memberIds yields a
  // set that contains only currentUserId — items will still match if the
  // server returned media for that user (e.g. before they were removed),
  // otherwise the empty state fires. We never mutate the stored filter.
  const visibleIds = useMemo(
    () => resolveVisibleIds(travelerFilter, memberIds, currentUserId),
    [travelerFilter, memberIds, currentUserId],
  );

  const items = (data?.items ?? []) as any[];
  const filtered = items.filter((it) => {
    if (visibleIds !== null && !visibleIds.has(it.user_id)) return false;
    if (filter === "photo" && it.media_type !== "photo") return false;
    if (filter === "video" && it.media_type !== "video") return false;
    if (filter === "heic" && !isHeic(it.original_filename)) return false;
    if (q && !(it.original_filename ?? "").toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  useEffect(() => {
    console.log("[admin-library] visible media count", filtered.length);
  }, [filtered.length]);

  const initials = (n?: string) =>
    (n ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("");

  const filterActive = travelerFilter.mode !== "all";

  return (
    <main className="mx-auto max-w-6xl px-5 md:px-8 py-6 md:py-8 pb-nav-safe">
      <PageHeader
        title="Library"
        subtitle={`${filtered.length} visible · ${items.length} total`}
      />

      {/* Toolbar */}
      <RoavaCard flush className="p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--roava-text-muted)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search filename"
              className="w-full h-9 pl-8 pr-3 rounded-md border border-[var(--roava-border)] bg-[var(--roava-card)] text-[13px] text-[var(--roava-text)] focus:outline-none focus:ring-1 focus:ring-[var(--roava-blue)]"
            />
          </div>
          <div className="flex gap-1">
            {(["all", "photo", "video", "heic"] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={[
                  "h-9 px-3 rounded-full text-[12.5px] border transition",
                  filter === f
                    ? "bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)] border-[var(--roava-blue)]"
                    : "bg-[var(--roava-card)] text-[var(--roava-text-muted)] border-[var(--roava-border)] hover:text-[var(--roava-text)] hover:bg-[var(--roava-gray)]/50",
                ].join(" ")}
              >
                {f === "all" ? "All" : f === "heic" ? "HEIC" : f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {travelerOptions.length > 0 ? (
            <TravelerVisibilityControl
              travelers={travelerOptions}
              meId={currentUserId}
              filter={travelerFilter}
              onChange={setTravelerFilter}
              labelPrefix="Admin filter: "
            />
          ) : null}
        </div>
      </RoavaCard>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-md bg-muted animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <RoavaCard flush className="border-dashed py-16 text-center text-sm text-[var(--roava-text-muted)]">
          {filterActive ? "No media for selected travelers" : "No media matches these filters."}
        </RoavaCard>
      ) : (
        <DayGroupedAdminGrid
          items={filtered}
          tripStartDate={tripData?.trip?.startDate ?? null}
          nameById={nameById}
          initials={initials}
          onSelect={setSelected}
        />
      )}

      <MediaPreviewDrawer item={selected} onClose={() => setSelected(null)} isAdmin />
    </main>
  );
}



function DayGroupedAdminGrid({
  items,
  tripStartDate,
  nameById,
  initials,
  onSelect,
}: {
  items: any[];
  tripStartDate: string | null;
  nameById: Map<string, string>;
  initials: (n?: string) => string;
  onSelect: (it: PreviewItem) => void;
}) {
  return (
    <DayCardGallery
      items={items}
      tripStartDate={tripStartDate}
      sort="captured_desc"
      nameById={nameById}
      renderGrid={(tiles) => (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {tiles.map((it: any) => {
            const heic = isHeic(it.original_filename);
            const isVideo = it.media_type === "video";
            const uploader = nameById.get(it.user_id) ?? "";
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => onSelect(it)}
                className="relative aspect-square rounded-md overflow-hidden bg-secondary border border-border hover:border-foreground/40 transition group text-left"
              >
                {isVideo && it.url ? (
                  <video
                    src={`${it.url}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                ) : it.media_type === "photo" ? (
                  <AdminThumb mediaId={it.id} />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[11px] text-muted-foreground p-2 text-center">
                    <ImageOff className="h-5 w-5" />
                  </div>
                )}

                <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-sm bg-black/65 backdrop-blur px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
                  {isVideo ? (
                    <>
                      <Play className="h-2.5 w-2.5 fill-current" />
                      Video
                    </>
                  ) : heic ? (
                    "HEIC"
                  ) : (
                    "Photo"
                  )}
                </span>

                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-1.5 py-1 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent">
                  <span className="rounded-sm bg-white/15 px-1 py-px font-medium tabular">
                    {initials(uploader) || "·"}
                  </span>
                  <span className="tabular opacity-90">{fmtBytes(it.file_size_bytes)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    />
  );
}

function AdminThumb({ mediaId }: { mediaId: string }) {
  const { url } = useThumbnailUrl(mediaId, 400);
  if (!url) {
    return (
      <div className="w-full h-full grid place-items-center text-[11px] text-muted-foreground p-2 text-center">
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

